import React, { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useAuthStatus, truncateText, formatDateDot, formatTimeHHMM } from '../../../../utils'
import styles from './ContractItem.module.scss'

// 계약 타입 및 지역 표시 렌더링
const renderContractTypeAndRegion = (contract) => {
	const { domesticContract, overseasContract, termContract, contract_type, contractTours } =
		contract
	const tourCount = contractTours?.length || 0

	// 국내 + 해외
	if (domesticContract !== null && overseasContract !== null) {
		return (
			<div className={styles.contractRegionAndType}>
				<div>국내</div>
				<div>해외</div>
				{contract_type === 'single' && <div>단일계약({tourCount}건)</div>}
				{contract_type === 'long_term' && <div>장기계약({tourCount}건)</div>}
				{contract_type === 'year_first_half' && <div>기간계약(상반기)</div>}
				{contract_type === 'year_second_half' && <div>기간계약(하반기)</div>}
				{contract_type === 'both' && <div>기간계약(상반기/하반기)</div>}
			</div>
		)
	}

	// 해외만
	if (domesticContract === null && overseasContract !== null) {
		return (
			<div className={styles.contractRegionAndType}>
				<div>해외</div>
				{contract_type === 'single' && <div>단일계약({tourCount}건)</div>}
				{contract_type === 'long_term' && <div>장기계약({tourCount}건)</div>}
				{contract_type === 'year_first_half' && <div>기간계약(상반기)</div>}
				{contract_type === 'year_second_half' && <div>기간계약(하반기)</div>}
				{contract_type === 'both' && <div>기간계약(상반기/하반기)</div>}
			</div>
		)
	}

	// 국내만
	if (domesticContract !== null && overseasContract === null) {
		return (
			<div className={styles.contractRegionAndType}>
				<div>국내</div>
				{contract_type === 'single' && <div>단일계약({tourCount}건)</div>}
				{contract_type === 'long_term' && <div>장기계약({tourCount}건)</div>}
				{contract_type === 'year_first_half' && <div>기간계약(상반기)</div>}
				{contract_type === 'year_second_half' && <div>기간계약(하반기)</div>}
				{contract_type === 'both' && <div>기간계약(상반기/하반기)</div>}
			</div>
		)
	}

	// 기간계약 (termContract)
	if (termContract !== null) {
		const isOverseasIncluded = termContract.isOverseasIncluded === true
		return (
			<div className={styles.contractRegionAndType}>
				<div>{isOverseasIncluded ? '해외포함' : '해외미포함'}</div>
				{contract_type === 'single' && <div>단일계약({tourCount}건)</div>}
				{contract_type === 'long_term' && <div>장기계약({tourCount}건)</div>}
				{contract_type === 'year_first_half' && <div>기간계약(상반기)</div>}
				{contract_type === 'year_second_half' && <div>기간계약(하반기)</div>}
				{contract_type === 'both' && <div>기간계약(상반기/하반기)</div>}
			</div>
		)
	}

	return null
}

// 계약 상태 배지 렌더링
const renderStatusBadge = (contractStatus) => {
	switch (contractStatus) {
		case 'PRE':
		case 'PRO_ACCEPTED':
			return <div className={styles.statusWait}>계약 대기</div>
		case 'ACCEPTED':
			return <div className={styles.statusComplete}>계약 체결</div>
		case 'CANCELED':
		case 'EXPIRED':
			return <div className={styles.statusEarlyTermination}>계약 취소</div>
		case 'TERMINATED':
			return <div className={styles.statusEarlyTermination}>계약 해지</div>
		default:
			return null
	}
}

const ContractItem = ({ contract, onContractClick }) => {
	const [auth] = useAuthStatus()

	// 상대방 이름 결정: 내가 프로면 캐디 이름, 내가 캐디면 프로 이름
	const counterpartName = useMemo(() => {
		if (!contract) return '이름 없음'

		const userType = auth.user?.userType

		// console.log(auth.user)

		if (userType === 'tourPro') {
			// 내가 프로면 상대방은 캐디
			return contract.caddie?.name || '이름 없음'
		}

		if (userType === 'tourCaddie') {
			// 내가 캐디면 상대방은 프로
			return contract.player?.name || '이름 없음'
		}

		// 기본값: 캐디나 프로 중 하나라도 있으면 그 이름 반환
		return contract.caddie?.name || contract.player?.name || '이름 없음'
	}, [auth.user?.user_type, contract?.caddie?.name, contract?.player?.name])

	// 계약 기간 계산
	const contractPeriod = useMemo(() => {
		// 기간계약일 때는 contract_started_at과 contract_ended_at 사용
		if (contract.termContract !== null) {
			if (contract.contract_started_at && contract.contract_ended_at) {
				return {
					startDate: new Date(contract.contract_started_at),
					endDate: new Date(contract.contract_ended_at),
				}
			}
			return { startDate: null, endDate: null }
		}

		// 기간계약이 아닐 때는 contractTours에서 계산
		const tourDates = contract.contractTours
		if (!tourDates || tourDates.length === 0) {
			return { startDate: null, endDate: null }
		}

		const startDates = tourDates.map((tour) => new Date(tour.tour.started_at))
		const endDates = tourDates.map((tour) => new Date(tour.tour.ended_at))
		const startDate = new Date(Math.min(...startDates))
		const endDate = new Date(Math.max(...endDates))

		return { startDate, endDate }
	}, [
		contract.contractTours,
		contract.termContract,
		contract.contract_started_at,
		contract.contract_ended_at,
	])

	// 계약 대회 정보
	const tournamentInfo = useMemo(() => {
		const tours = contract.contractTours || []
		if (tours.length === 0) {
			return '대회미정'
		}
		if (tours.length === 1) {
			return truncateText(tours[0].tour.name, 25)
		}
		return `${truncateText(tours[0].tour.name, 25)} 외 ${tours.length - 1}건`
	}, [contract.contractTours])

	const handleClick = () => {
		if (onContractClick) {
			onContractClick(contract.id)
		}
	}

	return (
		<Link to={`/contracts/${contract.id}`} onClick={handleClick}>
			<div
				className={`${styles.contractItem} ${
					contract.contract_status === 'REJECTED' ? styles.contractRejected : ''
				}`}>
				<div className={styles.contractItemHeader}>
					<span className={styles.contractItemHeaderName}>
						{counterpartName || '이름 없음'}님과의 투어캐디 계약
					</span>
					{renderStatusBadge(contract.contract_status)}
				</div>
				<div className={styles.contractItemBody}>
					<div className={styles.contractItemBodyElement}>
						<div>{'계약체결일'}</div>
						{contract.contract_status !== 'ACCEPTED' ? (
							<div>-</div>
						) : (
							<div>
								{formatDateDot(contract.document_completed_at)}{' '}
								{formatTimeHHMM(contract.document_completed_at)}
							</div>
						)}
					</div>
					<div className={styles.contractItemBodyElement}>
						<div>계약유형</div>
						{renderContractTypeAndRegion(contract)}
					</div>
					<div className={styles.contractItemBodyElement}>
						<div>계약기간</div>
						{contractPeriod.startDate && contractPeriod.endDate ? (
							<div>
								{formatDateDot(contractPeriod.startDate)} ~{' '}
								{formatDateDot(contractPeriod.endDate)}
							</div>
						) : (
							<div>-</div>
						)}
					</div>
					<div className={styles.contractItemBodyElement}>
						<div>계약대회</div>
						<div>{tournamentInfo}</div>
					</div>
				</div>
			</div>
		</Link>
	)
}

export default ContractItem
